import { ACCESS_RESOURCES, type ResourceId, pickPermissionKey } from '@/config/access-control'
import type { Permissions } from '@/rbac/grants'
import {
	type ViewGrantScope,
	getViewGrantScope,
	hasAnyViewGrant,
	viewGrantAllowsModule,
} from '@/utils/access-control/grantScope'

export type RouteAccessScenario =
	| 'route_denied'
	| 'empty_with_grant'
	| 'empty_scoped_grant'
	| 'api_forbidden'
	| 'api_forbidden_with_grant'

export type RouteAccessDeniedReason = 'no-permission' | 'not-owner' | 'not-admin'

const RESOURCE_LABELS: Record<ResourceId, string> = {
	projects: 'Projects',
	devices: 'Devices',
	dashboards: 'Dashboards',
	remote_commands: 'Remote Commands',
	custom_commands: 'Action Types',
	streams: 'Streams',
	metadata_keys: 'Metadata Keys',
	users: 'Users',
	roles: 'Roles',
	api_keys: 'API Keys',
	dbc_parsers: 'DBC Parsers',
	exporters: 'Exporters',
	subscriber_groups: 'Subscriber Groups',
	session_rules: 'Session Rules',
	firmware: 'Firmware',
	files: 'Files',
	json_config: 'JSON Config',
	geofence_config: 'Geofence Config',
	notifications: 'Notifications',
	project_settings: 'Project Settings',
	branding: 'Branding',
	ota_campaigns: 'OTA Campaigns',
	pull_based_actions: 'Pull-based Actions',
	dev_mode: 'Developer Mode',
	pushers: 'Pushers',
}

const CONTACT_ADMIN_SUFFIX =
	' If you believe you should have access, contact your administrator to verify your role permissions.'

export function getResourceLabel(resource: ResourceId): string {
	return RESOURCE_LABELS[resource] ?? resource
}

/** @deprecated Use getResourceLabel */
export const getResourceModuleLabel = getResourceLabel

export function getRouteAccessDeniedMessage(
	resource: ResourceId,
	reason: RouteAccessDeniedReason = 'no-permission',
): string {
	const label = getResourceLabel(resource)
	if (reason === 'not-owner') {
		return `You do not have permission to manage ${label} in this project.`
	}
	if (reason === 'not-admin') {
		return `Only project administrators can access ${label}.`
	}
	return `You do not have permission to view ${label}.${CONTACT_ADMIN_SUFFIX}`
}

export function getEmptyListAccessMessage(resource: ResourceId, scope: ViewGrantScope): string {
	const label = getResourceLabel(resource)
	if (scope === 'all') {
		return `No ${label} are available in this project yet, or the server returned no data. If you expected items here, contact your administrator.`
	}
	return `Your role includes ${label} access, but only for a limited set. Nothing in the current list matches that scope, or the server returned no data.${CONTACT_ADMIN_SUFFIX}`
}

export function getApiForbiddenMessage(resource: ResourceId, hasMatchingGrant: boolean): string {
	const label = getResourceLabel(resource)
	if (hasMatchingGrant) {
		return `The server denied this ${label} request even though your account shows related permissions. This can happen when grants are scoped or out of sync.${CONTACT_ADMIN_SUFFIX}`
	}
	return `You do not have permission to access ${label}.${CONTACT_ADMIN_SUFFIX}`
}

export function resolveRouteAccessMessage(input: {
	resource: ResourceId
	scenario: RouteAccessScenario
	reason?: RouteAccessDeniedReason
	viewGrantScope?: ViewGrantScope
}): string {
	const { resource, scenario, reason = 'no-permission', viewGrantScope } = input
	switch (scenario) {
		case 'route_denied':
			return getRouteAccessDeniedMessage(resource, reason)
		case 'empty_with_grant':
			return getEmptyListAccessMessage(resource, viewGrantScope ?? 'all')
		case 'empty_scoped_grant':
			return getEmptyListAccessMessage(resource, 'partial')
		case 'api_forbidden':
			return getApiForbiddenMessage(resource, false)
		case 'api_forbidden_with_grant':
			return getApiForbiddenMessage(resource, true)
	}
}

function grantForPermissionKey(permissions: Permissions | undefined, permission?: keyof Permissions): unknown {
	return permission ? permissions?.[permission] : undefined
}

export function getViewGrantScopeForResource(
	permissions: Permissions | undefined,
	_resource: ResourceId,
	viewPermission?: keyof Permissions,
	complexPermissionKey?: keyof Permissions,
): ViewGrantScope {
	const viewGrant = grantForPermissionKey(permissions, viewPermission)
	if (viewGrantAllowsModule(viewGrant)) {
		return getViewGrantScope(viewGrant)
	}
	return getViewGrantScope(grantForPermissionKey(permissions, complexPermissionKey))
}

export function getPermissionKeysForResource(
	_resource: ResourceId,
	viewPermission?: keyof Permissions,
	complexPermissionKey?: keyof Permissions,
): (keyof Permissions)[] {
	const keys: (keyof Permissions)[] = []
	if (viewPermission) {
		keys.push(viewPermission)
	}
	if (complexPermissionKey && complexPermissionKey !== viewPermission) {
		keys.push(complexPermissionKey)
	}
	return keys
}

export function classifyApiAccessFailure(input: {
	status: number
	permissions?: Permissions
	resource?: ResourceId
	viewPermission?: keyof Permissions
	complexPermissionKey?: keyof Permissions
}): RouteAccessScenario | null {
	const { status, permissions, resource, viewPermission, complexPermissionKey } = input
	if (status !== 403 || !resource) {
		return null
	}
	const keys = getPermissionKeysForResource(resource, viewPermission, complexPermissionKey)
	const hasGrant = keys.length > 0 && permissions ? hasAnyViewGrant(permissions, keys) : false
	return hasGrant ? 'api_forbidden_with_grant' : 'api_forbidden'
}

export function getApiAccessFailureMessage(input: {
	status: number
	permissions?: Permissions
	resource: ResourceId
	viewPermission?: keyof Permissions
	complexPermissionKey?: keyof Permissions
}): string | null {
	const scenario = classifyApiAccessFailure(input)
	if (!scenario) {
		return null
	}
	return resolveRouteAccessMessage({ resource: input.resource, scenario })
}

export function getEmptyResponseAccessMessage(input: {
	resource: ResourceId
	permissions?: Permissions
	viewPermission?: keyof Permissions
	complexPermissionKey?: keyof Permissions
	isEmpty: boolean
}): string | null {
	if (!input.isEmpty || !input.permissions) {
		return null
	}
	const scope = getViewGrantScopeForResource(
		input.permissions,
		input.resource,
		input.viewPermission,
		input.complexPermissionKey,
	)
	if (!viewGrantAllowsModule(scope)) {
		return null
	}
	const scenario: RouteAccessScenario = scope === 'partial' ? 'empty_scoped_grant' : 'empty_with_grant'
	return resolveRouteAccessMessage({ resource: input.resource, scenario, viewGrantScope: scope })
}

function viewPermissionKeysForResource(resource: ResourceId): {
	viewPermission?: keyof Permissions
	complexPermissionKey?: keyof Permissions
} {
	const cfg = ACCESS_RESOURCES[resource]
	return {
		viewPermission: pickPermissionKey(cfg, 'view'),
		complexPermissionKey: 'complexPermissionKey' in cfg ? cfg.complexPermissionKey : undefined,
	}
}

export function getEmptyResponseAccessMessageForResource(
	resource: ResourceId,
	permissions: Permissions | undefined,
	isEmpty: boolean,
): string | null {
	const keys = viewPermissionKeysForResource(resource)
	return getEmptyResponseAccessMessage({
		resource,
		permissions,
		...keys,
		isEmpty,
	})
}

export function getApiAccessFailureMessageForResource(
	status: number,
	resource: ResourceId,
	permissions: Permissions | undefined,
): string | null {
	const keys = viewPermissionKeysForResource(resource)
	return getApiAccessFailureMessage({
		status,
		resource,
		permissions,
		...keys,
	})
}
